import React from 'react';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import jsPDF from 'jspdf';
import toast from 'react-hot-toast';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import SelectBox from '../../components/SelectBox';
import NetworkError from '../../components/Error';
import classes from './DeviceVendors.module.css';
import axios from '../../utils/axiosConfig';

const FRONTEND_DEVICE_VENDORS_ERROR_CODES = {
  INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  PDF_EXPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
};

const buildDeviceVendorsSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Create dynamic chart options based on theme
const createChartOptions = (isDarkMode) => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  scales: {
    x: {
      beginAtZero: true,
      display: false,
      grid: {
        drawOnChartArea: false,
      },
      ticks: {
        font: {
          size: 10,
        },
        color: isDarkMode ? '#ffffff' : '#000000',
      },
    },
    y: {
      display: true,
      grid: {
        drawOnChartArea: false,
      },
      ticks: {
        font: {
          size: 12,
          weight: 500,
        },
        maxRotation: 0,
        minRotation: 0,
        color: isDarkMode ? '#ffffff' : '#000000', // Dynamic color based on theme
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    datalabels: {
      display: true,
      anchor: 'end',
      align: 'end',
      offset: 4,
      color: isDarkMode ? '#ffffff' : '#000000', // Dynamic color based on theme
      font: {
        size: 11,
        weight: 600,
        family: "'Poppins', sans-serif",
      },
    },
  },
  layout: {
    padding: {
      right: 60, // Increased padding for better label visibility
    },
  },
});

function createGradient(ctx, area) {
  const colorStart = 'rgba(255, 122, 0, 1)';
  const colorMid = 'rgba(192, 20, 18, 0.9)';
  const colorEnd = 'rgba(192, 20, 18, 1)';

  const gradient = ctx.createLinearGradient(0, area.bottom, 0, area.top);
  gradient.addColorStop(0, colorStart);
  gradient.addColorStop(0.5, colorMid);
  gradient.addColorStop(1, colorEnd);

  return gradient;
}

const NoChartData = () => (
  <div className={classes.noData}>
    <img src="/assets/no-chart-data.png" alt="No data available" />
    <p>No device vendor data available</p>
  </div>
);

const DeviceVendors = () => {
  const [loading, setLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [vendorData, setVendorData] = React.useState([]);
  const [topValue, setTopValue] = React.useState(10);
  const [supportErrorMessage, setSupportErrorMessage] = React.useState('');
  const [copiedSupportError, setCopiedSupportError] = React.useState(false);
  
  // Use existing watcher system from session instead of separate API call
  const [selectedWatcher, setSelectedWatcher] = React.useState('');
  const [selectedWatcherName, setSelectedWatcherName] = React.useState('');
  const [availableWatchers, setAvailableWatchers] = React.useState([]);
  
  const chartRef = React.useRef(null);
  const [chartData, setChartData] = React.useState({
    datasets: [],
  });
  const [data, setData] = React.useState({});

  // Detect dark mode
  const [isDarkMode, setIsDarkMode] = React.useState(false);

  const setDeviceVendorsSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    setSupportErrorMessage(
      buildDeviceVendorsSupportError(errorCode, pageInfo, sourceMessage)
    );
    setCopiedSupportError(false);
  }, []);

  const clearDeviceVendorsSupportError = React.useCallback((force = false) => {
    if (!force) return;
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = React.useCallback(async () => {
    if (!supportErrorMessage) return;
    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (error) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);

  React.useEffect(() => {
    // Check for dark mode
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(theme === 'dark');
    };

    // Initial check
    checkTheme();

    // Listen for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Watcher dropdown options, loaded once on mount. An empty watcher makes the
  // backend return just the list (no asset scan), so the selector populates
  // while the chart stays empty until the user picks a watcher.
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const fd = new FormData();
        fd.append('watchers', '');
        const response = await axios.post('/api/DeviceVendors', fd);
        if (alive && response.data && Array.isArray(response.data.watchers)) {
          setAvailableWatchers(response.data.watchers);
        }
      } catch (e) { /* leave the dropdown empty on failure */ }
    })();
    return () => { alive = false; };
  }, []);

  // Updated fetchVendorData to get both vendors and watchers
  const fetchVendorData = React.useCallback(async (isCancelled = () => false) => {
    if (!selectedWatcher) { setLoading(false); return; }   // nothing selected yet — render with no data
    setLoading(true);
    setHasError(false);
    setVendorData([]);   // clear the previous watcher's data immediately on switch

    try {
      // Always POST the selection (including 'all'): the backend treats an empty
      // watcher as the "options only" placeholder, so 'all' must be explicit.
      const formData = new FormData();
      formData.append('watchers', selectedWatcher);
      const response = await axios.post('/api/DeviceVendors', formData);

      if (isCancelled()) return;   // superseded by a newer selection

      if (response.data && response.data.status) {
        const vendors = response.data.vendors || [];

        // Update available watchers from API response (first call only)
        if (response.data.watchers && availableWatchers.length === 0) {
          setAvailableWatchers(response.data.watchers);
        }

        // The SQL query already provides [vendor_name, count] pairs
        // If topValue is 'all', show all vendors, otherwise limit to top N
        const sortedVendors = topValue === 'all'
          ? vendors.map(vendor => [vendor[0], vendor[1]])
          : vendors.slice(0, topValue).map(vendor => [vendor[0], vendor[1]]);

        setVendorData(sortedVendors);
        setHasError(false);
        clearDeviceVendorsSupportError(true);
      } else {
        setHasError(true);
        setDeviceVendorsSupportError(
          FRONTEND_DEVICE_VENDORS_ERROR_CODES.INITIAL_LOAD_FAILED,
          'Device Vendors Page / Vendor Data Load Failed',
          response?.data?.msg || 'Device vendors request did not return success'
        );
      }
    } catch (error) {
      if (isCancelled()) return;
      console.error('Error fetching device vendors:', error);
      setHasError(true);
      setDeviceVendorsSupportError(
        FRONTEND_DEVICE_VENDORS_ERROR_CODES.INITIAL_LOAD_FAILED,
        'Device Vendors Page / Vendor Data Request Error',
        error?.response?.data?.msg || error?.message || String(error)
      );
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [
    availableWatchers.length,
    clearDeviceVendorsSupportError,
    selectedWatcher,
    setDeviceVendorsSupportError,
    topValue,
  ]);

  // Fetch vendor data when dependencies change
  React.useEffect(() => {
    let cancelled = false;
    fetchVendorData(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchVendorData]);

  // Update chart data when vendor data changes
  React.useEffect(() => {
    const labels = [];
    const data = [];
    
    if (vendorData && vendorData.length > 0) {
      vendorData.forEach((vendor) => {
        labels.push(vendor[0]);
        data.push(vendor[1]);
      });
    }

    setData({
      labels,
      datasets: [
        {
          type: 'bar',
          fill: true,
          label: 'Device Count',
          data,
          borderColor: 'rgba(230, 111, 0, 1)',
          borderWidth: 0,
          borderRadius: 8,
        },
      ],
    });
  }, [vendorData]);

  // Apply gradient to chart when chart is ready
  React.useEffect(() => {
    const chart = chartRef.current;

    if (!chart || Object.keys(data).length === 0) {
      return;
    }

    const chartData = {
      ...data,
      datasets: data.datasets.map((dataset) => ({
        ...dataset,
        backgroundColor: createGradient(chart.ctx, chart.chartArea),
      })),
    };

    setChartData(chartData);
  }, [data]);

  // Handle watcher selection change
  const handleWatcherChange = (e) => {
    const watcherId = e.target.value;
    setSelectedWatcher(watcherId);
    
    if (watcherId === 'all') {
      setSelectedWatcherName('All Watchers');
    } else {
      // Find the watcher name from availableWatchers
      const watcher = availableWatchers.find(w => w[0] === watcherId);
      setSelectedWatcherName(watcher ? watcher[1] : watcherId);
    }
  };

  // Handle top value change
  const handleTopValueChange = (e) => {
    const value = e.target.value;
    setTopValue(value === 'all' ? 'all' : Number(value));
  };

  // Export chart to PDF
  const exportToPDF = () => {
    if (!vendorData || vendorData.length === 0) {
      toast.error('No chart data available to export');
      return;
    }

    try {
      // Get current date and time
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const timeStr = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      // Create PDF in landscape mode for better chart visibility
      const doc = new jsPDF('landscape');
      
      // Add title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      const title = topValue === 'all' ? 'All Device Vendors' : `Top ${topValue} Device Vendors`;
      doc.text(title, 14, 20);
      
      // Add subtitle with watcher info if filtered
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      let subtitle = 'Vendors by Device Count';
      if (selectedWatcher !== 'all') {
        subtitle += ` - Watcher: ${selectedWatcherName}`;
      }
      doc.text(subtitle, 14, 28);
      
      // Add date and time
      doc.setFontSize(10);
      doc.text(`Generated: ${dateStr} at ${timeStr}`, 14, 35);
      
      // Add total vendor count
      doc.text(`Total Vendors Displayed: ${vendorData.length}`, 14, 41);

      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 14;

      // Add data table for better readability
      let currentY = 50;
      
      // Table configuration
      const tableStartX = margin;
      const colWidths = {
        rank: 20,
        vendor: 140,
        count: 40
      };
      const tableWidth = colWidths.rank + colWidths.vendor + colWidths.count;
      const rowHeight = 8;
      const headerHeight = 10;
      
      // Draw table header
      doc.setFillColor(192, 20, 18); // Red color from theme
      doc.setTextColor(255, 255, 255);
      doc.rect(tableStartX, currentY, tableWidth, headerHeight, 'F');
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Rank', tableStartX + 3, currentY + 7);
      doc.text('Vendor Name', tableStartX + colWidths.rank + 3, currentY + 7);
      doc.text('Device Count', tableStartX + colWidths.rank + colWidths.vendor + 3, currentY + 7);
      
      currentY += headerHeight;
      
      // Draw table rows
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      
      // Determine how many rows fit on first page
      const availableHeight = pageHeight - currentY - 20; // Leave space for footer
      const rowsPerPage = Math.floor(availableHeight / rowHeight);
      
      vendorData.forEach((vendor, index) => {
        // Check if we need a new page
        if (index > 0 && index % rowsPerPage === 0) {
          // Add footer before new page
          doc.setFontSize(8);
          doc.setTextColor(128, 128, 128);
          doc.text('Generated by WatcherGUI', pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Page ${Math.floor(index / rowsPerPage)}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
          
          // Add new page
          doc.addPage();
          currentY = margin;
          
          // Redraw header on new page
          doc.setFillColor(192, 20, 18);
          doc.setTextColor(255, 255, 255);
          doc.rect(tableStartX, currentY, tableWidth, headerHeight, 'F');
          
          doc.setFontSize(11);
          doc.setFont(undefined, 'bold');
          doc.text('Rank', tableStartX + 3, currentY + 7);
          doc.text('Vendor Name', tableStartX + colWidths.rank + 3, currentY + 7);
          doc.text('Device Count', tableStartX + colWidths.rank + colWidths.vendor + 3, currentY + 7);
          
          currentY += headerHeight;
          doc.setTextColor(0, 0, 0);
          doc.setFont(undefined, 'normal');
          doc.setFontSize(9);
        }
        
        // Alternate row colors for better readability
        if (index % 2 === 0) {
          doc.setFillColor(245, 245, 245);
          doc.rect(tableStartX, currentY, tableWidth, rowHeight, 'F');
        }
        
        // Draw cell content
        const rank = (index + 1).toString();
        const vendorName = vendor[0] || 'Unknown';
        const count = vendor[1].toString();
        
        // Truncate vendor name if too long
        const maxVendorLength = 35;
        const displayVendor = vendorName.length > maxVendorLength 
          ? vendorName.substring(0, maxVendorLength - 3) + '...' 
          : vendorName;
        
        doc.text(rank, tableStartX + 3, currentY + 6);
        doc.text(displayVendor, tableStartX + colWidths.rank + 3, currentY + 6);
        doc.text(count, tableStartX + colWidths.rank + colWidths.vendor + 3, currentY + 6);
        
        currentY += rowHeight;
      });
      
      // Add footer to all pages
      const totalPages = doc.internal.pages.length - 1; // Subtract 1 for the first empty page
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text('Generated by WatcherGUI', pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }
      
      // Generate filename
      const watcherSuffix = selectedWatcher !== 'all' ? `_${selectedWatcherName.replace(/\s+/g, '_')}` : '';
      const filename = `Device_Vendors${watcherSuffix}_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
      
      // Save the PDF
      doc.save(filename);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      const sourceMessage = error?.message || String(error);
      const formattedError = buildDeviceVendorsSupportError(
        FRONTEND_DEVICE_VENDORS_ERROR_CODES.PDF_EXPORT_FAILED,
        'Device Vendors Page / PDF Export Failed',
        sourceMessage
      );
      toast.error(formattedError);
      setDeviceVendorsSupportError(
        FRONTEND_DEVICE_VENDORS_ERROR_CODES.PDF_EXPORT_FAILED,
        'Device Vendors Page / PDF Export Failed',
        sourceMessage
      );
    }
  };

  // Loading Bars Component
  const LoadingBars = () => (
    <div className={classes.loadingBars}>
      <div className={classes.loadingBarsTitle}>Loading Device Vendors...</div>
      <div className={classes.loadingBar}>
        <div className={classes.loadingBarFill}></div>
      </div>
      <div className={classes.loadingBar}>
        <div className={classes.loadingBarFill}></div>
      </div>
      <div className={classes.loadingBar}>
        <div className={classes.loadingBarFill}></div>
      </div>
      <div className={classes.loadingBar}>
        <div className={classes.loadingBarFill}></div>
      </div>
    </div>
  );

  // Add this function to determine chart size class
  const getChartSizeClass = (topValue) => {
    if (topValue === 'all') return 'auto'; // New class for all vendors
    if (topValue <= 5) return 'small';
    if (topValue <= 10) return 'medium';
    if (topValue <= 15) return 'large';
    if (topValue <= 20) return 'xlarge';
    return 'xxlarge'; // 30 vendors
  };

  // Add this function to calculate height
  const calculateChartHeight = (topValue, vendorCount = 0) => {
    if (topValue === 'all') {
      // For all vendors, calculate based on actual vendor count
      const baseHeight = 25; // Base height in rem
      const heightPerVendor = 1.8; // Height per vendor in rem
      return Math.max(30, baseHeight + vendorCount * heightPerVendor);
    }
    
    const baseHeight = 25; // Base height in rem
    const heightPerVendor = 2; // Additional height per vendor in rem
    return Math.max(baseHeight, baseHeight + (topValue - 5) * heightPerVendor);
  };

  // If there's a network error and no data, show error
  if (hasError && vendorData.length === 0) {
    return (
      <Content currentMenu='device-vendors' headerTitle='Device Vendors'>
        <NetworkError />
        {supportErrorMessage && (
          <div className={classes.supportErrorCard}>
            <strong>Support Error:</strong>
            <code className={classes.supportErrorCode}>{supportErrorMessage}</code>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button
                type='button'
                className={classes.copySupportErrorButton}
                onClick={handleCopySupportError}
              >
                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
              </button>
              <button
                type='button'
                className={classes.copySupportErrorButton}
                onClick={() => clearDeviceVendorsSupportError(true)}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </Content>
    );
  }

  return (
    <Content currentMenu='device-vendors' headerTitle='Device Vendors'>
      <div className={classes.container}>
        {supportErrorMessage && (
          <div className={classes.supportErrorBanner}>
            <div className={classes.supportErrorBannerText}>
              <strong>Support Error:</strong>
              <code className={classes.supportErrorCode}>{supportErrorMessage}</code>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button
                type='button'
                className={classes.copySupportErrorButton}
                onClick={handleCopySupportError}
              >
                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
              </button>
              <button
                type='button'
                className={classes.copySupportErrorButton}
                onClick={() => clearDeviceVendorsSupportError(true)}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        <div className={classes.vendorChart}>
          <div className={classes.card_heading}>
            <div className={classes.card_heading_left}>
              <h3 className={classes.card_heading_text}>
                {topValue === 'all' ? 'All Device Vendors' : `Top ${topValue} Device Vendors`}
                {selectedWatcher && selectedWatcher !== 'all' && (
                  <span className={classes.watcherIndicator}> - {selectedWatcherName}</span>
                )}
                {loading && <span className={classes.loadingIndicator}> (Loading...)</span>}
              </h3>
              <p className={classes.card_heading_subtext}>
                Vendors by Device Count
                {selectedWatcher && selectedWatcher !== 'all' && ` (Watcher: ${selectedWatcherName})`}
              </p>
            </div>
            
            {/* Move both dropdowns to the right side */}
            <div className={classes.dropdownContainer}>
              <div className={classes.dropdownGroup}>
                <label htmlFor="watcher-select" className={classes.dropdownLabel}>
                  Watcher:
                </label>
                <SelectBox
                  id="watcher-select"
                  style={{ width: '20rem' }}
                  value={selectedWatcher}
                  onChange={handleWatcherChange}
                  disabled={loading}
                >
                  <option value="">Select a Watcher</option>
                  <option value="all">All Watchers</option>
                  {availableWatchers.map((watcher) => (
                    <option key={watcher[0]} value={watcher[0]}>
                      {watcher[1]}
                    </option>
                  ))}
                </SelectBox>
              </div>
              
              <div className={classes.dropdownGroup}>
                <label htmlFor="vendor-select" className={classes.dropdownLabel}>
                  Show:
                </label>
                <SelectBox
                  id="vendor-select"
                  style={{ width: '20rem' }}
                  value={topValue}
                  onChange={handleTopValueChange}
                  disabled={loading}
                >
                  <option value={5}>Top 5 Vendors</option>
                  <option value={10}>Top 10 Vendors</option>
                  <option value={15}>Top 15 Vendors</option>
                  <option value={20}>Top 20 Vendors</option>
                  <option value={30}>Top 30 Vendors</option>
                  <option value="all">All Vendors</option>
                </SelectBox>
              </div>

              <div className={classes.exportButtonGroup}>
                <button 
                  className={classes.exportButton}
                  onClick={exportToPDF}
                  disabled={loading || !vendorData || vendorData.length === 0}
                >
                  Export PDF
                </button>
              </div>
            </div>
          </div>
          
          <div 
            className={`${classes.chart_box} ${classes[getChartSizeClass(topValue)]}`}
            style={{ height: `${calculateChartHeight(topValue, vendorData.length)}rem` }}
          >
            {loading ? (
              <div className={classes.loadingOverlay}>
                <LoadingBars />
              </div>
            ) : vendorData && vendorData.length > 0 ? (
              <Chart 
                ref={chartRef} 
                type='bar' 
                data={chartData} 
                options={createChartOptions(isDarkMode)} 
              />
            ) : (
              <NoChartData />
            )}
          </div>
        </div>
      </div>
    </Content>
  );
};

export default DeviceVendors;
